import { Button, Input } from 'antd';
import React, { useEffect, useState } from 'react';

import Modal from '../../foundation/components/modal/Modal';
import RichTextEditor, {
  editorStateToHtml,
  htmlToEditorState,
} from '../../foundation/components/rich_text_editor/RichTextEditor';
import { useUpdateSampleDetailMutation } from '../api/variantApi';
import { formatVariantHgvs } from '../probands_samples/utils';
import type { SampleDetail } from './types';

type SampleDetailEditModalProps = {
  open: boolean;
  sample?: SampleDetail;
  onClose: () => void;
};

const SampleDetailEditModal = ({
  open,
  sample,
  onClose,
}: SampleDetailEditModalProps) => {
  const [updateSampleDetail, { isLoading }] = useUpdateSampleDetailMutation();
  const [indicationEditorState, setIndicationEditorState] = useState(() =>
    htmlToEditorState(sample?.indication || ''),
  );
  const [commentEditorState, setCommentEditorState] = useState(() =>
    htmlToEditorState(sample?.comment || ''),
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setIndicationEditorState(htmlToEditorState(sample?.indication || ''));
    setCommentEditorState(htmlToEditorState(sample?.comment || ''));
  }, [open, sample?.comment, sample?.indication]);

  const handleSave = async () => {
    if (!sample) {
      return;
    }

    await updateSampleDetail({
      sample_id: sample.id,
      requestData: {
        indication: editorStateToHtml(indicationEditorState),
        comment: editorStateToHtml(commentEditorState),
      },
    }).unwrap();
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Edit Sample"
      onCancel={onClose}
      footer={null}
      width={760}
      className="sample-detail-edit-modal"
    >
      <div className="sample-detail-edit-modal__field">
        <div className="sample-detail-edit-modal__label">Sample ID:</div>
        <Input value={sample?.sample_id ?? ''} disabled />
      </div>

      <div className="sample-detail-edit-modal__field">
        <div className="sample-detail-edit-modal__label">Subject ID:</div>
        <div className="sample-detail-edit-modal__input-link">
          {sample?.subject.externalid ?? ''}
        </div>
      </div>

      <div className="sample-detail-edit-modal__field">
        <div className="sample-detail-edit-modal__label">Indication:</div>
        <RichTextEditor
          editorState={indicationEditorState}
          onChange={setIndicationEditorState}
          placeholder="Enter indication"
        />
      </div>

      <div className="sample-detail-edit-modal__field">
        <div className="sample-detail-edit-modal__label">Variants:</div>
        <div className="sample-detail-edit-modal__variants">
          {(sample?.genotype_variants ?? []).map((variant) => (
            <div
              className="sample-detail-edit-modal__variant-row"
              key={variant.genotype_id}
            >
              {variant.variant_id ? (
                <a
                  className="sample-detail-edit-modal__variant-link"
                  href={`/variant-workflow/${variant.variant_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {formatVariantHgvs(variant)}
                </a>
              ) : (
                <span className="sample-detail-edit-modal__variant-link">
                  {formatVariantHgvs(variant)}
                </span>
              )}
              <span className="sample-detail-edit-modal__variant-interpretation">
                {variant.clinical_interpretation || 'to be classified'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="sample-detail-edit-modal__field">
        <div className="sample-detail-edit-modal__label">Comments:</div>
        <RichTextEditor
          editorState={commentEditorState}
          onChange={setCommentEditorState}
          placeholder=""
        />
      </div>

      <div className="sample-detail-edit-modal__actions">
        <Button type="primary" loading={isLoading} onClick={handleSave}>
          Save changes
        </Button>
      </div>
    </Modal>
  );
};

export default SampleDetailEditModal;
